import { useEffect, useState } from 'react';
import {
  AGENT_OPEN_EVENT,
  AGENT_STATUS_EVENT,
  OPEN_AGENT_EVENT,
  type AgentStatus,
} from '../../lib/agentEvents';
import { IconButton } from '../ui';

const AGENT_STATUS_LABEL: Record<AgentStatus, string> = {
  idle: 'Idle',
  ready: 'Ready',
  working: 'Working…',
  blocked: 'Needs approval',
};

const LABEL_VISIBILITY = {
  sm: 'hidden sm:inline',
  xl: 'hidden xl:inline',
} as const;

// This trigger renders `children` on `IconButton`, which per that primitive's
// own contract (see IconButton.tsx's `boxClass`) suppresses its `size`/`shape`
// box classes entirely — height here is fully owned by THIS file's own
// `className`, not IconButton's. So the control-height scale (tokens.css
// `--control-h-sm`/`--control-h-md`) has to be threaded through explicitly.
const HEIGHT_CLASS = {
  sm: 'h-control-sm',
  md: 'h-control',
} as const;

/**
 * Canonical shell/editor entry point for the persistent agent drawer.
 * Status, disclosure state, visual treatment and accessible naming stay in
 * one composite even when a full-screen workspace replaces AppShell chrome.
 */
export function AgentStatusTrigger({
  labelFrom = 'sm',
  size = 'md',
  testId,
}: {
  labelFrom?: keyof typeof LABEL_VISIBILITY;
  /** Matches `Button`/`ToolbarActions`' own size scale — the CALLER's row
   * decides this, since `TopBar.tsx` sits on `md` and
   * `TemplateEditorWorkspace.tsx`'s header sits on `sm` (story 2-95b resume:
   * this trigger previously hardcoded `h-control`, rendering 36px inside a
   * `size="sm"` (28px) row — every sibling control there, verified live). */
  size?: keyof typeof HEIGHT_CLASS;
  testId?: string;
}): JSX.Element {
  const [agentStatus, setAgentStatus] = useState<AgentStatus>('idle');
  const [agentOpen, setAgentOpen] = useState(false);

  useEffect(() => {
    const onStatus = (event: Event) => {
      const detail = (event as CustomEvent<AgentStatus>).detail;
      if (detail) setAgentStatus(detail);
    };
    const onOpen = (event: Event) => {
      setAgentOpen(Boolean((event as CustomEvent<boolean>).detail));
    };
    window.addEventListener(AGENT_STATUS_EVENT, onStatus);
    window.addEventListener(AGENT_OPEN_EVENT, onOpen);
    return () => {
      window.removeEventListener(AGENT_STATUS_EVENT, onStatus);
      window.removeEventListener(AGENT_OPEN_EVENT, onOpen);
    };
  }, []);

  const labelClass = LABEL_VISIBILITY[labelFrom];

  return (
    <IconButton
      onClick={() => window.dispatchEvent(new CustomEvent(OPEN_AGENT_EVENT))}
      className={`${HEIGHT_CLASS[size]} justify-center gap-2 rounded-lg border border-border bg-muted px-2.5 text-foreground transition hover:bg-muted/70 sm:gap-2.5 sm:px-3 xl:justify-start xl:px-4`}
      aria-label={`Open agent workspace — ${AGENT_STATUS_LABEL[agentStatus]}`}
      aria-expanded={agentOpen}
      data-testid={testId}
      icon={<span data-agent-status={agentStatus} className="agent-status-dot h-2 w-2 shrink-0 rounded-full" />}
    >
      <span className={`${labelClass} text-sm font-medium text-foreground`}>Agent</span>
      <span className={`${labelClass} text-xs text-muted-foreground`}>{AGENT_STATUS_LABEL[agentStatus]}</span>
    </IconButton>
  );
}
